// Sidebar.tsx
import { Box, IconButton, Tooltip, Typography, FormControlLabel, Stack, Switch, styled, useTheme } from '@mui/material';
import {
  Home as HomeIcon,
  Logout as LogoutIcon,
  Brightness4 as DarkIcon,
  Brightness7 as LightIcon,
  Info as InfoIcon,
  Settings as SettingsIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext';
import { AuthService } from '../../../../api/services/AuthService';
import { useColorMode } from '../../../../context/ThemeContext';
import { ReactNode, useMemo } from 'react';

type SidebarProps = {
  open: boolean;
  openWidth: number;
  closedWidth: number;
  isMobile: boolean;
  onClose: () => void;
};

type NavItem = {
  to: string;
  title: string;
  icon: ReactNode;
  exact?: boolean;
};

const ThemeSwitch = styled(Switch)(({ theme }) => ({
  width: 56,
  height: 32,
  padding: 7,
  '& .MuiSwitch-switchBase': {
    margin: 1,
    padding: 0,
    transform: 'translateX(4px)',
    '&.Mui-checked': {
      color: '#fff',
      transform: 'translateX(22px)',
      '& .MuiSwitch-thumb:before': {
        content: '"🌙"',
      },
      '& + .MuiSwitch-track': {
        opacity: 1,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[300] : theme.palette.grey[900],
    width: 24,
    height: 24,
    '&:before': {
      content: '"☀️"',
      position: 'absolute',
      width: '100%',
      height: '100%',
      left: 0,
      top: 0,
      display: 'grid',
      placeItems: 'center',
      fontSize: 12,
    },
  },
  '& .MuiSwitch-track': {
    borderRadius: 16,
    opacity: 1,
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
  },
}));

export default function Sidebar({ open, openWidth, closedWidth, isMobile, onClose }: SidebarProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toggleColorMode, mode } = useColorMode();
  const location = useLocation();
  const theme = useTheme();

  const handleLogout = async () => {
    try {
      await AuthService.postApiAuthLogout();
      logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const computedWidth = open ? openWidth : closedWidth;

  const items: NavItem[] = useMemo(
    () => [
      { to: '/', title: 'Főoldal', icon: <HomeIcon />, exact: true },
      { to: '/about', title: 'Rólunk', icon: <InfoIcon /> },
      { to: '/faq', title: 'GYIK', icon: <HelpIcon /> },
      { to: '/settings', title: 'Beállítások', icon: <SettingsIcon /> },
    ],
    []
  );

  const isActive = (path: string, exact?: boolean) => {
    if (exact) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const getItemSx = (active: boolean) => {
    const activeBg = mode === 'dark' ? 'rgba(99, 102, 241, 0.12)' : 'rgba(99, 102, 241, 0.14)';
    const hoverBg = mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';

    return {
      position: 'relative',
      color: 'inherit',
      mb: 1,
      width: '100%',
      justifyContent: open ? 'flex-start' : 'center',
      borderRadius: open ? 1.5 : '999px',
      px: open ? 1 : 0,
      backgroundColor: active ? activeBg : 'transparent',
      transition: 'background-color .2s ease, transform .15s ease, box-shadow .2s ease',
      '&::before': open
        ? {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 6,
            bottom: 6,
            width: 3,
            borderRadius: 2,
            background: active
              ? (t: any) => `linear-gradient(180deg, ${t.palette.primary.main}, ${t.palette.primary.light})`
              : 'transparent',
            transition: 'background .2s ease',
          }
        : {},
      '&:hover': {
        backgroundColor: active ? activeBg : hoverBg,
        transform: open ? 'translateX(2px)' : 'translateY(-1px)',
        boxShadow: active ? 3 : 1,
      },
      '& .MuiSvgIcon-root': {
        transition: 'color .2s ease, transform .2s ease',
        color: active ? 'primary.main' : 'inherit',
      },
      ...(active && { boxShadow: 2 }),
    } as const;
  };

  return (
    <Box
      role="navigation"
      aria-label="Oldalsáv"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        height: '100vh',
        width: isMobile ? openWidth : computedWidth,
        bgcolor: mode === 'dark' ? 'grey.900' : 'grey.100',
        color: mode === 'dark' ? 'grey.100' : 'grey.900',
        boxShadow: 3,
        // ↓ A sidebar legyen minden fölött
        zIndex: 1600,
        transition: 'transform .25s ease, width .25s ease, box-shadow .2s ease',
        display: 'flex',
        flexDirection: 'column',
        alignItems: open ? 'flex-start' : 'center',
        px: open ? 2 : 0,
        pt: 2,
        transform: isMobile ? (open ? 'translateX(0)' : `translateX(-100%)`) : 'translateX(0)',
      }}
      aria-hidden={isMobile && !open ? true : undefined}
    >
      {/* Logó */}
      <Box
        component="img"
        src="/reddit_analyzer.png"
        alt="Reddit Analyzer Logo"
        sx={{
          width: open ? 150 : 40,
          height: 'auto',
          mx: 'auto',
          mb: 2,
          transition: 'transform 0.25s ease, width 0.25s ease',
          transform: open ? 'scale(1.05)' : 'scale(1.0)',
        }}
      />

      {/* Felső menüelemek */}
      {items.map(({ to, title, icon, exact }) => {
        const active = isActive(to, exact);
        return (
          <Tooltip key={to} title={title} placement="right" disableHoverListener={open}>
            <IconButton
              component={Link}
              to={to}
              sx={getItemSx(active)}
              onClick={() => {
                if (isMobile) onClose();
              }}
            >
              {icon}
              {open && (
                <Typography
                  sx={{
                    ml: 1,
                    fontWeight: active ? 700 : 500,
                    color: active ? 'primary.main' : 'inherit',
                    transition: 'color .2s ease',
                  }}
                >
                  {title}
                </Typography>
              )}
            </IconButton>
          </Tooltip>
        );
      })}

      {/* --- LENTI RÉSZ: Téma kapcsoló + fiókinfó + logout --- */}
      <Box sx={{ mt: 'auto', width: '100%', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {/* Téma kapcsoló */}
        <Box
          sx={{
            width: '100%',
            px: open ? 1 : 0,
            display: 'flex',
            justifyContent: open ? 'flex-start' : 'center',
          }}
        >
          {open ? (
            <FormControlLabel
              control={<ThemeSwitch checked={mode === 'dark'} onChange={toggleColorMode} />}
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  {mode === 'dark' ? <DarkIcon fontSize="small" /> : <LightIcon fontSize="small" />}
                  <Typography variant="body2">{mode === 'dark' ? 'Sötét mód' : 'Világos mód'}</Typography>
                </Stack>
              }
              sx={{
                m: 0,
                px: 1,
                py: 0.5,
                borderRadius: 1.5,
                backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
                width: '100%',
                '& .MuiFormControlLabel-label': { width: '100%' },
              }}
            />
          ) : (
            <IconButton
              onClick={toggleColorMode}
              sx={{
                borderRadius: '999px',
                backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                '&:hover': {
                  backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                },
              }}
            >
              {mode === 'dark' ? <DarkIcon /> : <LightIcon />}
            </IconButton>
          )}
        </Box>

        {/* Bejelentkezve badge (csak nyitva) */}
        {open ? (
          <Box sx={{ width: '100%', px: 1 }}>
            <Box
              sx={{
                width: '100%',
                backgroundColor: mode === 'dark' ? 'grey.800' : 'grey.200',
                borderRadius: '8px',
                p: 1.5,
                boxSizing: 'border-box',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: mode === 'dark' ? 'grey.400' : 'grey.600',
                  fontWeight: 600,
                  display: 'block',
                  textAlign: 'left',
                }}
              >
                Bejelentkezve:
                <Box
                  component="span"
                  sx={{
                    display: 'block',
                    color: mode === 'dark' ? 'grey.300' : 'grey.800',
                    fontSize: '0.75rem',
                    fontStyle: 'italic',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user?.email}
                </Box>
              </Typography>
            </Box>
          </Box>
        ) : null}

        {/* Logout */}
        <Tooltip title="Kijelentkezés" placement="right" disableHoverListener={open}>
          <IconButton
            onClick={handleLogout}
            sx={{
              position: 'relative',
              color: 'inherit',
              width: '100%',
              mb: 1,
              justifyContent: open ? 'flex-start' : 'center',
              borderRadius: open ? 1.5 : '999px',
              px: open ? 1 : 0,
              backgroundColor: 'transparent',
              transition: 'background-color .2s ease, transform .15s ease, box-shadow .2s ease',
              '&:hover': {
                backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                transform: open ? 'translateX(2px)' : 'translateY(-1px)',
                boxShadow: 1,
              },
            }}
          >
            <LogoutIcon />
            {open && <Typography sx={{ ml: 1 }}>Kijelentkezés</Typography>}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
